import { type ChangeEvent, useCallback } from "react";
import { Button } from "#app/components/ui/button.js";
import { Icon } from "#app/components/ui/icon.js";
import { Spinner } from "#app/components/ui/spinner.js";
import { cn, getPostImgSrc } from "#app/utils/misc.js";
import { usePost } from "../../../../context/post-context";
import { useDropZone, useFileUpload, useUploader } from "./hooks";

export const ImageUploader = ({
  onUpload,
}: {
  onUpload: (url: string) => void;
}) => {
  const { loading, uploadFile, selectFile } = useUploader({ onUpload });
  const { handleUploadClick, ref } = useFileUpload();
  const { draggedInside, onDrop, onDragEnter, onDragLeave } = useDropZone({
    uploader: uploadFile,
  });
  const { allImages, postId } = usePost();

  const onFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      e.target.files ? uploadFile(e.target.files[0] as File) : null,
    [uploadFile],
  );

  if (loading) {
    return (
      <div className="flex min-h-[10rem] items-center justify-center rounded-lg bg-opacity-80 p-8">
        <Spinner className="text-neutral-500" size={1.5} />
      </div>
    );
  }

  const wrapperClass = cn(
    "flex flex-col items-center justify-center rounded-lg bg-opacity-80 p-xxs",
    draggedInside && "bg-neutral-100",
  );

  const imagesInThisPost = allImages?.filter(
    (image) => image.postId === postId,
  );
  const restImages = allImages?.filter((image) => image.postId !== postId);

  return (
    <div
      className={wrapperClass}
      onDrop={onDrop}
      onDragOver={onDragEnter}
      onDragLeave={onDragLeave}
      contentEditable={false}
    >
      <Icon
        name="lucide-image"
        className="mb-4 h-12 w-12 text-black opacity-20"
      />
      <div className="flex flex-col items-center justify-center gap-2">
        <div className="text-center text-sm font-medium text-neutral-400">
          {draggedInside ? "Suelta la imagen aquí" : "Arrastra y suelta o"}
        </div>
        <div>
          <Button
            disabled={draggedInside}
            onClick={handleUploadClick}
            size="sm"
          >
            <Icon name="lucide-image-up" />
            <span className={"!text-white"}>Sube una imagen</span>
          </Button>
        </div>
      </div>
      <input
        className="h-0 w-0 overflow-hidden opacity-0"
        ref={ref}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.gif"
        onChange={onFileChange}
      />

      {allImages && allImages.length > 0 && (
        <div className="mt-sm flex flex-col gap-sm">
          {imagesInThisPost && imagesInThisPost.length > 0 && (
            <div>
              <p className="text-sm font-semibold">Imágenes en este artículo</p>
              <div className="grid max-h-[250px] grid-cols-5 gap-xxs overflow-y-auto">
                {imagesInThisPost?.map((image) => (
                  <img
                    key={image.id}
                    src={getPostImgSrc(image.id)}
                    onClick={() => {
                      selectFile(`/resources/post-images/${image.id}`);
                    }}
                    className="h-8 w-8 cursor-pointer rounded-lg border border-neutral-200 hover:border-neutral-300"
                  />
                ))}
              </div>
            </div>
          )}
          {restImages && restImages.length > 0 && (
            <div>
              <p className="text-sm font-semibold">Otras imágenes subidas</p>
              <div className="grid max-h-[250px] grid-cols-5 gap-xxs overflow-y-auto">
                {restImages?.map((image) => (
                  <img
                    key={image.id}
                    src={getPostImgSrc(image.id)}
                    onClick={() => {
                      selectFile(`/resources/post-images/${image.id}`);
                    }}
                    className="h-8 w-8 cursor-pointer rounded-lg border border-neutral-200 hover:border-neutral-300"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
